const PAYMENTS_PER_YEAR = {
    monthly: 12,
    "semi-monthly": 24,
    "bi-weekly": 26,
    weekly: 52,
    "accelerated-bi-weekly": 26,
    "accelerated-weekly": 52,
};
export function paymentsPerYear(frequency) {
    return PAYMENTS_PER_YEAR[frequency];
}
/** The interest rate per payment period, from a nominal annual rate. */
export function periodicRate(annualRate, compounding, perYear) {
    const c = compounding === "semi-annual" ? 2 : 12;
    return Math.pow(1 + annualRate / c, c / perYear) - 1;
}
/** Level payment that clears `principal` over `count` periods at `rate`. */
export function levelPayment(principal, rate, count) {
    if (count <= 0)
        return principal;
    if (rate === 0)
        return principal / count;
    return (principal * rate) / (1 - Math.pow(1 + rate, -count));
}
/** The regular payment for a mortgage, in cents. */
export function mortgagePayment(terms) {
    const compounding = terms.compounding ?? "semi-annual";
    const { frequency } = terms;
    if (frequency === "accelerated-bi-weekly" || frequency === "accelerated-weekly") {
        const monthly = levelPayment(terms.principal, periodicRate(terms.annualRate, compounding, 12), terms.amortizationYears * 12);
        return Math.round(frequency === "accelerated-bi-weekly" ? monthly / 2 : monthly / 4);
    }
    const perYear = PAYMENTS_PER_YEAR[frequency];
    return Math.round(levelPayment(terms.principal, periodicRate(terms.annualRate, compounding, perYear), terms.amortizationYears * perYear));
}
/**
 * Full amortisation schedule. `prepayments` maps a period number (1-based)
 * to an extra lump sum paid with that period's payment.
 */
export function amortizationSchedule(terms, prepayments = new Map()) {
    const compounding = terms.compounding ?? "semi-annual";
    const perYear = PAYMENTS_PER_YEAR[terms.frequency];
    const rate = periodicRate(terms.annualRate, compounding, perYear);
    const payment = mortgagePayment(terms);
    const rows = [];
    let balance = terms.principal;
    let totalInterest = 0;
    let totalPaid = 0;
    const maxPeriods = Math.ceil(terms.amortizationYears * perYear * 2) + 1;
    for (let period = 1; balance > 0 && period <= maxPeriods; period++) {
        const interest = Math.round(balance * rate);
        let pay = Math.min(payment, balance + interest);
        let principalPart = pay - interest;
        if (principalPart <= 0 && pay < balance + interest) {
            throw new Error("Payment does not cover interest; the balance would never go down.");
        }
        let prepayment = Math.min(prepayments.get(period) ?? 0, balance - principalPart);
        if (prepayment < 0)
            prepayment = 0;
        // The payment is rounded to the cent, which leaves a few dollars over at
        // the end; like lenders do, fold a small remainder into the last payment.
        const residual = balance - principalPart - prepayment;
        if (residual > 0 && residual < payment / 10) {
            pay += residual;
            principalPart += residual;
        }
        balance = balance - principalPart - prepayment;
        if (balance < 0) {
            pay += balance;
            principalPart += balance;
            balance = 0;
        }
        totalInterest += interest;
        totalPaid += pay + prepayment;
        rows.push({ period, payment: pay, interest, principal: principalPart, prepayment, balance });
    }
    return { payment, rows, totalInterest, totalPaid, periods: rows.length };
}
//# sourceMappingURL=amortization.js.map